import { ItemTypeEnum } from "@jasper/client-models";
import { SaveableCard } from "@jasper/forms";
import { useItemMaterialsByItemType } from "@jasper/use-cases";
import { Box, Group, SegmentedControl, Stack, Text } from "@mantine/core";
import { IconPalette } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { withForm } from "../../../hooks/preferenceForm";
import { usePreferenceCardSave } from "../../../hooks/usePreferenceCardSave";
import { getGeneralPreferenceDefaults } from "../generalPreferenceForm";
import { ItemShadeType } from "/@/generated/graphql";

// Same rule as the order flow: a guide is offered when the catalogue has
// shades for the family, labelled with the order flow's translations
const useShadeGuideOptions = () => {
  const { t } = useTranslation("product-workflows");
  const crown = useItemMaterialsByItemType({ itemType: ItemTypeEnum.crown });
  const fullDenture = useItemMaterialsByItemType({
    itemType: ItemTypeEnum.fullDenture,
  });
  const partialDenture = useItemMaterialsByItemType({
    itemType: ItemTypeEnum.partialDenture,
  });

  const toOptions = (shadeGuides: { value: string }[]) =>
    [...new Set(shadeGuides.map(({ value }) => value))].map(value => ({
      value: value as ItemShadeType,
      label: t(`shadeTypes.${value}`, {
        defaultValue: value.replaceAll("_", " "),
      }),
    }));

  return {
    fixed: toOptions(crown.itemShadeTypes),
    removable: toOptions([
      ...fullDenture.itemShadeTypes,
      ...partialDenture.itemShadeTypes,
    ]),
  };
};

export const ShadePreferences = withForm({
  defaultValues: getGeneralPreferenceDefaults(undefined),
  render: function Render({ form }) {
    const { status, save } = usePreferenceCardSave(form, [
      "fixedShadeType",
      "removableShadeType",
    ]);
    const shadeGuideOptions = useShadeGuideOptions();

    return (
      <SaveableCard
        title="Teintiers par défaut"
        description="Un teintier par famille · proposé dans chaque flux"
        status={status}
        onSave={save}
      >
        <Stack gap="s3">
          <Text variant="strong">Prothèses fixes</Text>
          <Group>
            <Box display="flex" style={{ alignItems: "center" }}>
              <IconPalette size={15} color="var(--mantine-color-brand-4)" />
              <Text variant="caption" fw="500" ml="s1">
                TEINTIER
              </Text>
            </Box>
            <form.Field name="fixedShadeType">
              {field => (
                <SegmentedControl
                  data={shadeGuideOptions.fixed}
                  onChange={value => field.handleChange(value)}
                  value={field.state.value}
                />
              )}
            </form.Field>
          </Group>
          <Text variant="strong">Prothèses amovibles</Text>
          <Group>
            <Box display="flex" style={{ alignItems: "center" }}>
              <IconPalette size={15} color="var(--mantine-color-brand-4)" />
              <Text variant="caption" fw="500" ml="s1">
                TEINTIER
              </Text>
            </Box>
            <form.Field name="removableShadeType">
              {field => (
                <SegmentedControl
                  data={shadeGuideOptions.removable}
                  onChange={field.handleChange}
                  value={field.state.value}
                />
              )}
            </form.Field>
          </Group>
        </Stack>
      </SaveableCard>
    );
  },
});
